import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter from "../../common/DateRangeFilter";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import { fetchSharedWithMeDocuments } from "./qmsService";
import { apiMessage, isApiSuccess } from "./qmsApiUtils";
import { getQmsAccessState, useQmsAccess } from "./qmsPermissionUtils";
import { getQmsScreen } from "./qmsScreenConfig";
import {
  applySharedWithMeFilters,
  buildSharedWithMeOwnerOptions,
  createInitialSharedWithMeFilters,
  DEFAULT_SHARED_WITH_ME_COLUMNS,
  getSharedWithMeDisplayStage,
  getSharedWithMeDocuments,
  getSharedWithMeFieldLabel,
  getSharedWithMeQueueLabel,
  getSharedWithMeQueueTone,
  getSharedWithMeSentDate,
  groupSharedWithMeDocuments,
  isSharedWithMeFolderRow,
  SHARED_WITH_ME_COLUMN_DATE_PRESETS,
  SHARED_WITH_ME_FIELD_OPTIONS,
} from "./qmsSharedWithMeUtils";
import {
  getVaultDocumentDisplayName,
  getVaultDocumentListId,
  getVaultStageClass,
} from "./qmsVaultUtils";
import QmsVaultStaffAvatar from "./QmsVaultStaffAvatar";
import QmsSharedWithMeWorkspaceModal from "./QmsSharedWithMeWorkspaceModal";
import "./QmsPages.scss";

const screen = getQmsScreen("sharedWithMe");

const SHARED_QUEUE_FILTER_OPTIONS = [
  { value: "reviewer", label: "Reviewer" },
  { value: "approval", label: "Approval" },
];

function QmsSharedWithMe() {
  const { userData, loading: userLoading } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { canQmsReviewDocuments, canQmsApproveDocuments } = useQmsAccess();

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [tableFilters, setTableFilters] = useState(createInitialSharedWithMeFilters);
  const [workspaceModal, setWorkspaceModal] = useState({ open: false, row: null });

  const userDataRef = useRef(userData);
  const showAlertRef = useRef(showAlert);
  userDataRef.current = userData;
  showAlertRef.current = showAlert;

  const staffId = userData?.StaffId || "";
  const isSystemAdmin = userData?.Role === "System Administrator";
  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: screen?.permissionKey || "QMS_SHARED_WITH_ME",
    loading: userLoading,
  });

  useEffect(() => {
    if (access.status !== "allowed" || !userData?.OrganizationId || !userData?.StaffId) {
      return undefined;
    }

    let cancelled = false;
    const loadDocuments = async () => {
      setDocumentsLoading(true);
      try {
        const response = await fetchSharedWithMeDocuments(userDataRef.current);
        if (cancelled) return;
        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Failed to load shared documents.");
        }
        setDocuments(getSharedWithMeDocuments(response));
      } catch (error) {
        if (cancelled) return;
        showAlertRef.current({
          title: "Error",
          message: error?.message || "Failed to load shared documents.",
          type: "danger",
        });
        setDocuments([]);
      } finally {
        if (!cancelled) setDocumentsLoading(false);
      }
    };

    loadDocuments();
    return () => {
      cancelled = true;
    };
  }, [access.status, refreshToken, userData?.OrganizationId, userData?.StaffId]);

  const groupedSections = useMemo(() => groupSharedWithMeDocuments(documents), [documents]);

  const counts = useMemo(() => {
    const review =
      groupedSections.find((section) => section.id === "review")?.documents.length || 0;
    const approval =
      groupedSections.find((section) => section.id === "approval")?.documents.length || 0;
    return { review, approval };
  }, [groupedSections]);

  const baseRows = useMemo(
    () => documents.filter((row) => !isSharedWithMeFolderRow(row)),
    [documents],
  );

  const ownerFilterOptions = useMemo(
    () => buildSharedWithMeOwnerOptions(baseRows),
    [baseRows],
  );

  const reportRows = useMemo(
    () => applySharedWithMeFilters(baseRows, tableFilters),
    [baseRows, tableFilters],
  );

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const openDocument = useCallback((row) => {
    setWorkspaceModal({ open: true, row });
  }, []);

  const closeDocument = useCallback(() => {
    setWorkspaceModal({ open: false, row: null });
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshToken((token) => token + 1);
  }, []);

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "DocumentTitle") {
        const title = getVaultDocumentDisplayName(row);
        return (
          <button
            type="button"
            className="report-table__cell-link qms-doc-lib__doc-title-link"
            onClick={() => openDocument(row)}
            title={title}
          >
            {title}
          </button>
        );
      }

      if (field === "DocumentId") {
        const docId = getVaultDocumentListId(row);
        return (
          <span className="qms-doc-lib__doc-id" title={docId}>
            {docId}
          </span>
        );
      }

      if (field === "SharedQueue") {
        const tone = getSharedWithMeQueueTone(row);
        const label = getSharedWithMeQueueLabel(row);
        return (
          <span className={`qms-shared-with-me__queue-pill is-${tone}`}>{label}</span>
        );
      }

      if (field === "DocumentStage") {
        const stage = getSharedWithMeDisplayStage(row);
        if (!stage) return "—";
        return (
          <span className={`qms-vault__stage-pill ${getVaultStageClass(stage)}`}>{stage}</span>
        );
      }

      if (field === "CreatedBy") {
        const owner = String(row.CreatedBy || "").trim();
        if (!owner) return "—";
        return (
          <span className="qms-shared-with-me__owner-cell">
            <QmsVaultStaffAvatar name={owner} compact title={owner} />
          </span>
        );
      }

      if (field === "SentDate" || field === "SystemDateTime") {
        return getSharedWithMeSentDate(row);
      }

      return String(row[field] ?? "—");
    },
    [openDocument],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    // Owner + Sent are filtered via BadgeMultiSelect / DateRangeFilter + applySharedWithMeFilters.
    if (field === "CreatedBy" || field === "SentDate") {
      return true;
    }

    const raw = String(searchValue || "").trim();
    if (!raw) return true;

    if (field === "DocumentId") {
      const listId = getVaultDocumentListId(row).toLowerCase();
      const term = raw.toLowerCase();
      const digits = term.replace(/\D/g, "");
      if (digits) {
        return listId.replace(/\D/g, "").includes(digits) || listId.includes(term);
      }
      return listId.includes(term);
    }

    if (field === "DocumentTitle") {
      return getVaultDocumentDisplayName(row).toLowerCase().includes(raw.toLowerCase());
    }

    if (field === "SharedQueue") {
      const tone = getSharedWithMeQueueTone(row);
      if (raw === "reviewer") return tone === "review";
      if (raw === "approval") return tone === "approval";
      return true;
    }

    if (field === "DocumentStage") {
      return getSharedWithMeDisplayStage(row).toLowerCase().includes(raw.toLowerCase());
    }

    return String(row[field] ?? "")
      .toLowerCase()
      .includes(raw.toLowerCase());
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DocumentId") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="search"
            className="report-table__column-search qms-doc-lib__column-filter"
            placeholder="Search ID"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          />
        );
      }

      if (field === "SharedQueue") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          >
            <option value="">All queues</option>
            {SHARED_QUEUE_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "CreatedBy") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-filter-multiselect">
            <BadgeMultiSelect
              options={ownerFilterOptions}
              value={tableFilters.owners}
              onChange={(values) => updateTableFilters({ owners: values })}
              emptyLabel="All owners"
              addLabel="+"
              popoverTitle="Filter by owner"
              popoverSubtitle="Select one or more owners"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "SentDate") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={tableFilters.sentDateRange}
              onChange={(range) => updateTableFilters({ sentDateRange: range })}
              placeholder="Sent date range"
              presetOptions={SHARED_WITH_ME_COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      return null;
    },
    [
      ownerFilterOptions,
      tableFilters.owners,
      tableFilters.sentDateRange,
      updateTableFilters,
    ],
  );

  const layoutStorageKey = useMemo(
    () => (staffId ? `qms-shared-with-me-layout-v2-${staffId}` : ""),
    [staffId],
  );

  const breadcrumbItems = useMemo(
    () => [{ label: "Home", path: "/dashboard" }, { label: screen.title }],
    [],
  );

  const breadcrumbActions = useMemo(
    () => [
      {
        icon: `fas fa-sync-alt${documentsLoading ? " fa-spin" : ""}`,
        label: "Refresh",
        onClick: handleRefresh,
      },
    ],
    [documentsLoading, handleRefresh],
  );

  const breadcrumbQueueAlert = useMemo(
    () => (
      <div
        className="qms-shared-with-me__queue-alert"
        role="status"
        aria-label="Queue summary"
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
        <span className="qms-shared-with-me__queue-alert-text">
          Documents assigned to you for review or approval.
        </span>
        <div className="qms-shared-with-me__stats">
          {canQmsReviewDocuments ? (
            <span className="qms-shared-with-me__stat is-review">
              <strong>{counts.review}</strong> Reviewer
            </span>
          ) : null}
          {canQmsApproveDocuments ? (
            <span className="qms-shared-with-me__stat is-approval">
              <strong>{counts.approval}</strong> Approval
            </span>
          ) : null}
        </div>
      </div>
    ),
    [
      canQmsApproveDocuments,
      canQmsReviewDocuments,
      counts.approval,
      counts.review,
    ],
  );

  if (access.status === "loading") return null;

  if (access.status === "denied") {
    return (
      <div className="user-mgmt-page qms-shared-with-me qms-document-library">
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="alert alert-warning" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      </div>
    );
  }

  return (
    <div className="user-mgmt-page qms-shared-with-me qms-document-library">
      <InfoAlert {...alertState} onClose={hideAlert} />

      <PageBreadcrumb
        items={breadcrumbItems}
        actions={breadcrumbActions}
        trailingExtras={breadcrumbQueueAlert}
      />

      <div className="qms-shared-with-me__panel">
        <div className="qms-doc-lib__report-wrap">
          <ReportTable
            rows={reportRows}
            fieldOptions={SHARED_WITH_ME_FIELD_OPTIONS}
            defaultColumns={DEFAULT_SHARED_WITH_ME_COLUMNS}
            getFieldLabel={getSharedWithMeFieldLabel}
            renderCell={renderReportCell}
            renderColumnSearch={renderColumnSearch}
            matchColumnSearch={matchColumnSearch}
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
            persistLayoutKey={layoutStorageKey}
            loading={documentsLoading}
          />
        </div>
      </div>

      <QmsSharedWithMeWorkspaceModal
        open={workspaceModal.open}
        row={workspaceModal.row}
        userData={userData}
        onClose={closeDocument}
        onNotify={showAlert}
        onSubmitted={handleRefresh}
      />
    </div>
  );
}

export default QmsSharedWithMe;
